/**
 * Device Identification Utility
 * 
 * For mobile apps (React Native), this can be extended to get actual MAC address.
 * For web browsers, we generate a device fingerprint since MAC addresses are not accessible.
 */

/**
 * Generate a more robust hash from a string
 * Uses a better hashing algorithm for uniqueness
 */
const hashString = (str) => {
  let hash = 0;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
};

/**
 * Generate a SHA-256 like hash (simplified version for browser compatibility)
 */
const generateHash = (input) => {
  // Use a combination of multiple hash functions for better uniqueness
  let hash1 = 0;
  let hash2 = 5381;
  
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash1 = ((hash1 << 5) - hash1) + char;
    hash1 = hash1 & hash1;
    hash2 = ((hash2 << 5) + hash2) + char;
  }
  
  return Math.abs(hash1).toString(36) + Math.abs(hash2).toString(36);
};

/**
 * Get canvas fingerprint (may fail on some mobile browsers)
 */
const getCanvasFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    
    ctx.textBaseline = 'top';
    ctx.font = '14px "Arial"';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Device fingerprint', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Device fingerprint', 4, 17);
    
    return canvas.toDataURL();
  } catch (error) {
    console.warn('Canvas fingerprinting not available:', error);
    return null;
  }
};

/**
 * Get WebGL fingerprint (more reliable on mobile)
 */
const getWebGLFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return null;
    
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) + 
             gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    }
    return gl.getParameter(gl.VENDOR) + gl.getParameter(gl.RENDERER);
  } catch (error) {
    console.warn('WebGL fingerprinting not available:', error);
    return null;
  }
};

/**
 * Generate a device fingerprint for web browsers
 * Combines multiple browser/device characteristics to create a unique identifier
 * More robust for mobile browsers
 */
export const getDeviceFingerprint = () => {
  // First, check if we already have a stored fingerprint
  const storedFingerprint = localStorage.getItem('deviceFingerprint');
  if (storedFingerprint) {
    return storedFingerprint;
  }
  
  try {
    // Collect stable device characteristics
    const nav = navigator;
    const screenData = window.screen || {};
    
    // Get canvas fingerprint (may be null on some browsers)
    const canvasFp = getCanvasFingerprint();
    
    // Get WebGL fingerprint (more reliable on mobile)
    const webglFp = getWebGLFingerprint();
    
    // Collect all available characteristics
    const characteristics = [
      nav.userAgent || '',
      nav.language || '',
      nav.languages ? nav.languages.join(',') : '',
      nav.platform || '',
      nav.hardwareConcurrency || '0',
      nav.deviceMemory || '0',
      nav.maxTouchPoints || '0',
      screenData.width + 'x' + screenData.height,
      screenData.availWidth + 'x' + screenData.availHeight,
      screenData.colorDepth || '0',
      screenData.pixelDepth || '0',
      new Date().getTimezoneOffset().toString(),
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      window.devicePixelRatio || '1',
      canvasFp ? canvasFp.substring(0, 100) : '',
      webglFp || '',
      // Add a persistent ID if available
      localStorage.getItem('devicePersistentId') || '',
    ].filter(Boolean).join('|');
    
    // Generate a more robust hash
    const fingerprint = generateHash(characteristics);
    
    // Store in multiple places for redundancy
    try {
      localStorage.setItem('deviceFingerprint', fingerprint);
      // Also store in sessionStorage as backup
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('deviceFingerprint', fingerprint);
      }
      // Create a persistent ID for future consistency
      if (!localStorage.getItem('devicePersistentId')) {
        const persistentId = 'pid-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('devicePersistentId', persistentId);
      }
    } catch (storageError) {
      console.warn('Could not store fingerprint in storage:', storageError);
    }
    
    return fingerprint;
  } catch (error) {
    console.error('Error generating device fingerprint:', error);
    
    // Fallback: use a combination of available data
    const fallback = [
      navigator.userAgent || 'unknown',
      navigator.platform || 'unknown',
      (window.screen?.width || 0) + 'x' + (window.screen?.height || 0),
      new Date().getTimezoneOffset().toString(),
    ].join('-');
    
    // Try to get from sessionStorage as last resort
    const sessionFp = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('deviceFingerprint') : null;
    if (sessionFp) {
      return sessionFp;
    }
    
    const fallbackFingerprint = generateHash(fallback);
    
    // Try to store it
    try {
      localStorage.setItem('deviceFingerprint', fallbackFingerprint);
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('deviceFingerprint', fallbackFingerprint);
      }
    } catch (e) {
      // Storage might be full or disabled
      console.warn('Could not store fallback fingerprint:', e);
    }
    
    return fallbackFingerprint;
  }
};

/**
 * Get MAC address (for mobile apps - Capacitor/Android)
 * In web browsers, this will return null and we'll use device fingerprint instead
 */
export const getMacAddress = async () => {
  const { Capacitor } = await import('@capacitor/core');
  const isNative = Capacitor.isNativePlatform();
  
  if (!isNative) {
    // For web browsers, MAC address is not accessible for security reasons
    return null;
  }

  try {
    // In native Android app, try to get MAC address via Device plugin
    // Note: MAC address access is restricted on Android 6.0+
    // We'll use Android ID as primary identifier instead
    
    // Try to get from device info service
    const deviceInfoService = (await import('../services/deviceInfoService')).default;
    const deviceInfo = await deviceInfoService.getDeviceInfo();
    
    return deviceInfo.macAddress || null;
  } catch (error) {
    console.warn('Could not get MAC address:', error);
    return null;
  }
};

/**
 * Debug utility to check device identification status
 * Useful for troubleshooting device fingerprint issues
 */
export const debugDeviceId = () => {
  const info = {
    hasLocalStorage: typeof localStorage !== 'undefined',
    hasSessionStorage: typeof sessionStorage !== 'undefined',
    storedFingerprint: null,
    sessionFingerprint: null,
    storedMacAddress: null,
    storedFallbackId: null,
    canvasAvailable: false,
    webglAvailable: false,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    screenSize: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
  };
  
  try {
    if (info.hasLocalStorage) {
      info.storedFingerprint = localStorage.getItem('deviceFingerprint');
      info.storedMacAddress = localStorage.getItem('deviceMacAddress');
      info.storedFallbackId = localStorage.getItem('deviceFallbackId');
    }
    
    if (info.hasSessionStorage) {
      info.sessionFingerprint = sessionStorage.getItem('deviceFingerprint');
    }
    
    // Test canvas
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      info.canvasAvailable = !!ctx;
    } catch (e) {
      info.canvasAvailable = false;
    }
    
    // Test WebGL
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      info.webglAvailable = !!gl;
    } catch (e) {
      info.webglAvailable = false;
    }
  } catch (error) {
    info.error = error.message;
  }
  
  return info;
};

/**
 * Get stable device ID from IndexedDB (most persistent storage)
 */
const getStableIdFromIndexedDB = () => {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    
    try {
      const request = indexedDB.open('DeviceStorage', 1);
      
      request.onerror = () => {
        resolve(null);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('device')) {
          db.createObjectStore('device');
        }
      };
      
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['device'], 'readonly');
        const store = transaction.objectStore('device');
        const getRequest = store.get('stableId');
        
        getRequest.onsuccess = () => {
          resolve(getRequest.result || null);
        };
        
        getRequest.onerror = () => {
          resolve(null);
        };
      };
    } catch (e) {
      resolve(null);
    }
  });
};

/**
 * Store stable device ID in IndexedDB
 */
const storeStableIdInIndexedDB = (stableId) => {
  if (typeof indexedDB === 'undefined') return;
  
  try {
    const request = indexedDB.open('DeviceStorage', 1);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('device')) {
        db.createObjectStore('device');
      }
    };
    
    request.onsuccess = (event) => {
      const db = event.target.result;
      const transaction = db.transaction(['device'], 'readwrite');
      const store = transaction.objectStore('device');
      store.put(stableId, 'stableId');
    };
  } catch (e) {
    // Silently fail - IndexedDB might not be available
  }
};

/**
 * Generate a UUID-like identifier (acts like a MAC address)
 * This creates a stable, unique identifier that persists across sessions
 * For mobile devices, this acts as a MAC address replacement
 */
const generateStableDeviceId = async () => {
  // Try to get existing stable ID from multiple sources (in order of persistence)
  let stableId = null;
  
  // 1. Try localStorage first (fastest)
  try {
    stableId = localStorage.getItem('deviceStableId');
  } catch (e) {
    // localStorage might be disabled
  }
  
  // 2. Try sessionStorage
  if (!stableId && typeof sessionStorage !== 'undefined') {
    try {
      stableId = sessionStorage.getItem('deviceStableId');
    } catch (e) {
      // sessionStorage might be disabled
    }
  }
  
  // 3. Try IndexedDB (most persistent, but async)
  if (!stableId) {
    stableId = await getStableIdFromIndexedDB();
    // If found in IndexedDB, restore to localStorage for faster access
    if (stableId) {
      try {
        localStorage.setItem('deviceStableId', stableId);
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('deviceStableId', stableId);
        }
      } catch (e) {
        // Can't store, but we have the ID
      }
    }
  }
  
  // If no stable ID exists, create one (UUID-like format)
  if (!stableId) {
    // Generate a UUID v4-like identifier
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    
    stableId = uuid;
    
    // Store it in multiple places for redundancy
    try {
      localStorage.setItem('deviceStableId', stableId);
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('deviceStableId', stableId);
      }
      // Store in IndexedDB (most persistent)
      storeStableIdInIndexedDB(stableId);
    } catch (e) {
      console.warn('Could not store stable device ID:', e);
      // Still return the ID even if we can't store it
    }
  }
  
  return stableId;
};

/**
 * Get device identifier - returns MAC address if available, otherwise device ID
 * For Android devices, uses Android ID as primary identifier
 * Always returns a consistent identifier for the same device
 */
export const getDeviceId = async () => {
  try {
    const { Capacitor } = await import('@capacitor/core');
    const isNative = Capacitor.isNativePlatform();
    
    if (isNative) {
      // For native Android app, use Device Info Service
      const deviceInfoService = (await import('../services/deviceInfoService')).default;
      const identifier = await deviceInfoService.getDeviceIdentifier();
      
      return {
        mac_address: identifier.mac_address,
        device_fingerprint: identifier.device_fingerprint,
        android_id: identifier.android_id,
        serial_number: identifier.serial_number,
      };
    }
    
    // For web browsers, use stable device ID
    const macAddress = await getMacAddress();
    if (macAddress) {
      // Store MAC address for consistency
      try {
        localStorage.setItem('deviceMacAddress', macAddress);
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('deviceMacAddress', macAddress);
        }
      } catch (e) {
        console.warn('Could not store MAC address:', e);
      }
      return { 
        mac_address: macAddress, 
        device_fingerprint: null,
        android_id: null,
        serial_number: null,
      };
    }
    
    // For web browsers, use a stable UUID-like identifier
    const stableDeviceId = await generateStableDeviceId();
    
    return { 
      mac_address: null, 
      device_fingerprint: stableDeviceId,
      android_id: null,
      serial_number: null,
    };
    
  } catch (error) {
    console.error('Error getting device ID:', error);
    
    // Last resort: try to get any stored ID
    try {
      const stored = localStorage.getItem('deviceStableId') || 
                    (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('deviceStableId') : null);
      if (stored) {
        return { 
          mac_address: null, 
          device_fingerprint: stored,
          android_id: null,
          serial_number: null,
        };
      }
    } catch (e) {
      // Storage not available
    }
    
    // Generate a temporary identifier
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return { 
      mac_address: null, 
      device_fingerprint: tempId,
      android_id: null,
      serial_number: null,
    };
  }
};

